import { useEffect, useMemo, useRef, useState } from 'react';
import { InputAdornment, InputBase, Popover, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import magnifying_glass from '../../assets/images/magnifying_glass.svg';
import './Screener.css';

// Same categorization logic as EV table (FiltersContainer.js) – Type → Time → markets
const categorizeMarkets = (marketOptions) => {
	const markets = marketOptions.map((o) => (typeof o === 'string' ? o : o?.value ?? o?.label));
	const categorized = {};
	const seen = new Set();

	markets.forEach((market) => {
		if (!market || seen.has(market)) return;
		seen.add(market);
		let type = 'Other';
		const marketLower = market.toLowerCase();

		if (
			marketLower.includes('player') ||
			marketLower.includes('fantasy score') ||
			marketLower.includes('scorer') ||
			marketLower.includes('batsman') ||
			marketLower.includes('bowler') ||
			marketLower.includes('man of the match') ||
			marketLower.includes('first goal scorer') ||
			marketLower.includes('last goal scorer') ||
			marketLower.includes('second goal scorer') ||
			marketLower.includes('third goal scorer') ||
			marketLower.includes('first touchdown scorer') ||
			marketLower.includes('last touchdown scorer') ||
			marketLower.includes('first basket') ||
			marketLower.includes('first try scorer') ||
			marketLower.includes('first card receiver') ||
			marketLower.includes('anytime card receiver') ||
			marketLower.includes('anytime red card receiver') ||
			marketLower.includes('first card') ||
			marketLower.includes('player assists') ||
			marketLower.includes('player goals') ||
			marketLower.includes('player points') ||
			marketLower.includes('player rebounds') ||
			marketLower.includes('player saves') ||
			marketLower.includes('player shots') ||
			marketLower.includes('player passes') ||
			marketLower.includes('player tackles') ||
			marketLower.includes('player blocks') ||
			marketLower.includes('player steals') ||
			marketLower.includes('player runs') ||
			marketLower.includes('player hits') ||
			marketLower.includes('player strikeouts') ||
			marketLower.includes('player aces') ||
			marketLower.includes('player cards') ||
			marketLower.includes('player fouls') ||
			marketLower.includes('player interceptions') ||
			marketLower.includes('player sacks') ||
			marketLower.includes('player touchdowns') ||
			marketLower.includes('player yards') ||
			marketLower.includes('player fantasy')
		) {
			type = 'Player Prop';
		} else if (marketLower.includes('moneyline') || marketLower.includes('draw no bet')) {
			type = 'Moneyline';
		} else if (
			marketLower.includes('spread') ||
			marketLower.includes('handicap') ||
			marketLower.includes('puck line') ||
			marketLower.includes('run line') ||
			marketLower.includes('point spread') ||
			marketLower.includes('goal spread') ||
			marketLower.includes('asian handicap') ||
			marketLower.includes('game spread') ||
			marketLower.includes('set handicap') ||
			marketLower.includes('round handicap') ||
			marketLower.includes('map handicap') ||
			marketLower.includes('kills handicap') ||
			marketLower.includes('aces handicap') ||
			marketLower.includes('cards handicap') ||
			marketLower.includes('corners handicap')
		) {
			type = 'Spread';
		} else if (marketLower.includes('total') || marketLower.includes('over/under') || marketLower.includes('odd/even')) {
			type = 'Total';
		} else if (
			marketLower.includes('team total') ||
			marketLower.includes('team to score') ||
			marketLower.includes('team to win') ||
			marketLower.includes('team clean sheet') ||
			marketLower.includes('first team to score') ||
			marketLower.includes('last team to score') ||
			marketLower.includes('team highest') ||
			marketLower.includes('team with most') ||
			marketLower.includes('team top')
		) {
			type = 'Team';
		} else if (marketLower.includes('race to')) {
			type = 'Race To';
		} else {
			type = 'Other';
		}

		let time = 'Game';
		if (marketLower.includes('inning') || marketLower.includes('innings')) {
			time = 'Inning';
		} else if (
			/[0-9]+(st|nd|rd|th)\s+set/i.test(marketLower) ||
			(marketLower.includes('set') &&
				!marketLower.includes('handicap') &&
				!marketLower.includes('set handicap') &&
				(marketLower.includes('1st set') ||
					marketLower.includes('2nd set') ||
					marketLower.includes('3rd set') ||
					marketLower.includes('4th set') ||
					marketLower.includes('5th set')))
		) {
			time = 'Set';
		} else if (
			/[0-9]+(st|nd|rd|th)\s+([0-9]+\s+)?maps?/i.test(marketLower) ||
			(marketLower.includes('map') &&
				!marketLower.includes('handicap') &&
				!marketLower.includes('map handicap') &&
				(marketLower.includes('1st map') ||
					marketLower.includes('2nd map') ||
					marketLower.includes('3rd map') ||
					marketLower.includes('4th map') ||
					marketLower.includes('5th map') ||
					marketLower.includes('1st 2 maps') ||
					marketLower.includes('1st 3 maps') ||
					marketLower.includes('1st 4 maps') ||
					marketLower.includes('maps player') ||
					/[0-9]+(st|nd|rd|th)\s+[0-9]+\s+maps/i.test(marketLower)))
		) {
			time = 'Map';
		} else if (
			marketLower.includes('1st half') ||
			marketLower.includes('1st quarter') ||
			marketLower.includes('1st period') ||
			marketLower.includes('1st 10 minutes') ||
			marketLower.includes('1st 5 minutes') ||
			marketLower.includes('1st 3 quarters') ||
			marketLower.includes('1st round') ||
			marketLower.includes('1st 3 holes') ||
			marketLower.includes('1st x overs')
		) {
			if (marketLower.includes('1st half')) time = '1st Half';
			else if (marketLower.includes('1st quarter')) time = '1st Quarter';
			else if (marketLower.includes('1st period')) time = '1st Period';
			else if (marketLower.includes('1st 10 minutes')) time = '1st 10 Mins';
			else if (marketLower.includes('1st 5 minutes')) time = '1st 5 Mins';
			else if (marketLower.includes('1st 3 quarters')) time = '1st 3 Quarter';
			else if (marketLower.includes('1st round')) time = '1st Round';
			else if (marketLower.includes('1st 3 holes')) time = '1st 3 Holes';
			else if (marketLower.includes('1st x overs')) time = '1st X Overs';
			else time = '1st Half';
		} else if (
			marketLower.includes('2nd half') ||
			marketLower.includes('2nd quarter') ||
			marketLower.includes('2nd period') ||
			marketLower.includes('2nd round')
		) {
			if (marketLower.includes('2nd half')) time = '2nd Half';
			else if (marketLower.includes('2nd quarter')) time = '2nd Quarter';
			else if (marketLower.includes('2nd period')) time = '2nd Period';
			else if (marketLower.includes('2nd round')) time = '2nd Round';
			else time = '2nd Half';
		} else if (marketLower.includes('3rd quarter') || marketLower.includes('3rd period')) {
			time = marketLower.includes('3rd quarter') ? '3rd Quarter' : '3rd Period';
		} else if (marketLower.includes('4th quarter')) {
			time = '4th Quarter';
		} else if (marketLower.includes('reg time') || marketLower.includes('no ot') || marketLower.includes('(no ot)')) {
			time = 'Game';
		} else if (
			marketLower.includes('single over') ||
			marketLower.includes('1st over') ||
			marketLower.includes('2nd over') ||
			marketLower.includes('over team total')
		) {
			time = 'Single Over';
		} else if (marketLower.includes('1st 3 quarter')) {
			time = '1st 3 Quarter';
		} else if (marketLower.includes('golf')) {
			time = 'Golf';
		} else {
			time = 'Game';
		}

		if (!categorized[type]) categorized[type] = {};
		if (!categorized[type][time]) categorized[type][time] = [];
		categorized[type][time].push({ label: market, value: market });
	});
	return categorized;
};

const typeOrder = ['Moneyline', 'Spread', 'Total', 'Team', 'Player Prop', 'Race To', 'Other'];
const timeOrder = [
	'Game',
	'1st Half',
	'2nd Half',
	'1st Quarter',
	'2nd Quarter',
	'3rd Quarter',
	'4th Quarter',
	'1st 3 Quarter',
	'1st Period',
	'2nd Period',
	'3rd Period',
	'1st 10 Mins',
	'1st 5 Mins',
	'Set',
	'Inning',
	'Map',
	'Golf',
	'Single Over',
	'1st X Overs',
	'1st 2 innings',
	'1st 4 innings',
	'1st 7 innings',
	'1st 6 innings',
	'1st Round',
	'1st 3 Holes',
	'2nd Round',
];

const sortTimes = (times) => {
	return [...times].sort((a, b) => {
		const indexA = timeOrder.indexOf(a);
		const indexB = timeOrder.indexOf(b);
		if (indexA === -1 && indexB === -1) return a.localeCompare(b);
		if (indexA === -1) return 1;
		if (indexB === -1) return -1;
		return indexA - indexB;
	});
};

export const ScreenerMarketsDropdown = (props) => {
	const {
		options,
		value,
		whichFiltersOpen,
		setWhichFiltersOpen,
		filters,
		setFilters,
		filterName,
		filterTitle,
		filterPlaceholder,
		setRefreshCounter,
		refreshCounter,
	} = props;

	const [anchorEl, setAnchorEl] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [expandedTypes, setExpandedTypes] = useState({});
	const [expandedTimes, setExpandedTimes] = useState({});
	const prevSearchTermRef = useRef('');
	const savedStateRef = useRef({ types: {}, times: {} });
	const currentExpandedTypesRef = useRef({});
	const currentExpandedTimesRef = useRef({});

	const isOpen = Boolean(anchorEl);
	const currentValue = value ?? filters?.[filterName] ?? '';

	const optionsWithShape = useMemo(
		() => options?.map((o) => (typeof o === 'string' ? { label: o, value: o } : { label: o.label ?? o.value, value: o.value ?? o.label })) ?? [],
		[options]
	);

	const categorizedMarkets = useMemo(() => categorizeMarkets(optionsWithShape), [optionsWithShape]);

	const filteredCategorized = useMemo(() => {
		if (!searchTerm) return categorizedMarkets;
		const filtered = {};
		Object.keys(categorizedMarkets).forEach((type) => {
			filtered[type] = {};
			Object.keys(categorizedMarkets[type]).forEach((time) => {
				const markets = categorizedMarkets[type][time].filter((m) =>
					m.label.toLowerCase().includes(searchTerm.toLowerCase())
				);
				if (markets.length > 0) filtered[type][time] = markets;
			});
			if (Object.keys(filtered[type]).length === 0) delete filtered[type];
		});
		return filtered;
	}, [searchTerm, categorizedMarkets]);

	const sortedTypes = useMemo(
		() =>
			Object.keys(filteredCategorized).sort((a, b) => {
				const indexA = typeOrder.indexOf(a);
				const indexB = typeOrder.indexOf(b);
				if (indexA === -1 && indexB === -1) return a.localeCompare(b);
				if (indexA === -1) return 1;
				if (indexB === -1) return -1;
				return indexA - indexB;
			}),
		[filteredCategorized]
	);

	useEffect(() => {
		currentExpandedTypesRef.current = expandedTypes;
	}, [expandedTypes]);
	useEffect(() => {
		currentExpandedTimesRef.current = expandedTimes;
	}, [expandedTimes]);

	useEffect(() => {
		const wasSearching = prevSearchTermRef.current.length > 0;
		const isSearching = searchTerm.length > 0;
		if (isSearching && !wasSearching) {
			savedStateRef.current.types = { ...currentExpandedTypesRef.current };
			savedStateRef.current.times = { ...currentExpandedTimesRef.current };
			const allTypes = Object.keys(filteredCategorized);
			const allTimes = {};
			allTypes.forEach((type) => {
				allTimes[type] = Object.keys(filteredCategorized[type]);
			});
			setExpandedTypes(Object.fromEntries(allTypes.map((t) => [t, true])));
			setExpandedTimes(
				Object.fromEntries(
					Object.entries(allTimes).flatMap(([type, times]) => times.map((time) => [`${type}-${time}`, true]))
				)
			);
		} else if (isSearching && wasSearching) {
			const allTypes = Object.keys(filteredCategorized);
			const allTimes = {};
			allTypes.forEach((type) => {
				allTimes[type] = Object.keys(filteredCategorized[type]);
			});
			setExpandedTypes(Object.fromEntries(allTypes.map((t) => [t, true])));
			setExpandedTimes(
				Object.fromEntries(
					Object.entries(allTimes).flatMap(([type, times]) => times.map((time) => [`${type}-${time}`, true]))
				)
			);
		} else if (!isSearching && wasSearching) {
			setExpandedTypes(savedStateRef.current.types);
			setExpandedTimes(savedStateRef.current.times);
		}
		prevSearchTermRef.current = searchTerm;
	}, [searchTerm, filteredCategorized]);

	const toggleType = (type) => {
		setExpandedTypes((prev) => ({ ...prev, [type]: prev[type] !== true }));
	};
	const toggleTime = (typeTime) => {
		setExpandedTimes((prev) => ({ ...prev, [typeTime]: prev[typeTime] !== true }));
	};

	const handleOpen = (e) => {
		e.stopPropagation();
		const copy = JSON.parse(JSON.stringify(whichFiltersOpen || {}));
		copy[filterName] = true;
		setWhichFiltersOpen?.(copy);
		setAnchorEl(e.currentTarget);
		setSearchTerm('');
	};

	const handleClose = () => {
		setAnchorEl(null);
		setSearchTerm('');
		const copy = JSON.parse(JSON.stringify(whichFiltersOpen || {}));
		copy[filterName] = false;
		setWhichFiltersOpen?.(copy);
	};

	const handleSelect = (market) => {
		const copy = JSON.parse(JSON.stringify(filters || {}));
		copy[filterName] = market.value;
		setFilters(copy);
		if (typeof setRefreshCounter === 'function' && typeof refreshCounter === 'number') {
			setRefreshCounter(refreshCounter + 1);
		}
		handleClose();
	};


	return (
		<>
			<div
				className="flex flex-col screener-market-trigger-wrap"
				style={{
					minWidth: 160,
					width: 'max-content',
				}}
			>
				<div
					onClick={handleOpen}
					role="button"
					tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							handleOpen(e);
						}
					}}
					style={{
						border: `1px solid ${isOpen ? 'var(--screener-accent, #3b82f6)' : 'var(--screener-border-strong, rgba(255,255,255,0.1))'}`,
						minHeight: 52,
						padding: '8px 12px',
						borderRadius: 'var(--screener-radius-sm, 8px)',
						display: 'flex',
						flexWrap: 'nowrap',
						gap: 8,
						alignItems: 'center',
						backgroundColor: isOpen ? 'rgba(59, 130, 246, 0.08)' : 'var(--screener-surface, #16181d)',
						cursor: 'pointer',
					}}
				>
					<div className="flex flex-row screener-market-trigger-inner" style={{ width: '100%', alignItems: 'center', minWidth: 0 }}>
						{!currentValue && (
							<div className="cursor-pointer mr-2" style={{ fontSize: 'inherit', color: 'var(--screener-text-muted)', whiteSpace: 'nowrap' }}>
								{filterTitle}
							</div>
						)}
						{currentValue && (
							<div style={{ padding: 4, paddingRight: 10, paddingLeft: 10, cursor: 'pointer', color: 'var(--screener-text)', whiteSpace: 'nowrap' }}>
								{currentValue}
							</div>
						)}
					</div>
				</div>
			</div>

			<Popover
				open={isOpen}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				PaperProps={{
					className: 'screener-popover-paper',
					sx: {
						maxHeight: 420,
						minWidth: 320,
						width: 340,
						p: 0,
						overflow: 'hidden',
						borderRadius: 10,
						backgroundColor: 'var(--screener-surface, #16181d)',
						border: '1px solid var(--screener-border-strong, rgba(255,255,255,0.1))',
					},
				}}
			>
				<div style={{ padding: 12 }}>
					<div
						className="flex flex-row items-center gap-2"
						style={{
							border: '1px solid var(--screener-border-strong)',
							borderRadius: 8,
							padding: '8px 12px',
							backgroundColor: 'var(--screener-bg, #0f1115)',
							marginBottom: 12,
						}}
					>
						<img src={magnifying_glass} alt="search" style={{ width: 16, height: 16, opacity: 0.7 }} />
						<InputBase
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							placeholder={`Search for a ${filterPlaceholder}`}
							onClick={(e) => e.stopPropagation()}
							inputProps={{ style: { color: 'var(--screener-text)', fontSize: 14 } }}
							sx={{ flex: 1 }}
						/>
					</div>

					<div style={{ maxHeight: 340, overflowY: 'auto' }}>
						{sortedTypes.length === 0 ? (
							<Typography variant="body2" sx={{ color: 'var(--screener-text-muted)', py: 2, px: 1 }}>
								No markets match your search.
							</Typography>
						) : (
							sortedTypes.map((type) => {
								const isTypeExpanded = expandedTypes[type] === true;
								const times = sortTimes(Object.keys(filteredCategorized[type]));
								return (
									<div
										key={type}
										style={{
											border: '1px solid var(--screener-border-strong)',
											borderRadius: 8,
											overflow: 'hidden',
											marginBottom: 8,
										}}
									>
										<div
											onClick={() => toggleType(type)}
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												padding: '10px 12px',
												cursor: 'pointer',
												backgroundColor: 'var(--screener-surface-elevated, #1c1f26)',
											}}
										>
											<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
												<svg
													style={{
														width: 16,
														height: 16,
														color: 'var(--screener-text-muted)',
														transform: isTypeExpanded ? 'rotate(90deg)' : 'none',
														transition: 'transform 0.15s ease',
													}}
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
														clipRule="evenodd"
													/>
												</svg>
												<span style={{ fontSize: 14, fontWeight: 600, color: 'var(--screener-text)' }}>{type}</span>
												<span style={{ fontSize: 12, color: 'var(--screener-text-muted)' }}>
													({Object.values(filteredCategorized[type]).flat().length})
												</span>
											</div>
										</div>
										{isTypeExpanded && (
											<div style={{ backgroundColor: 'var(--screener-surface)' }}>
												{times.map((time) => {
													const typeTimeKey = `${type}-${time}`;
													const isTimeExpanded = expandedTimes[typeTimeKey] === true;
													const markets = filteredCategorized[type][time];
													return (
														<div
															key={typeTimeKey}
															style={{ borderTop: '1px solid var(--screener-border-strong)' }}
														>
															<div
																onClick={() => toggleTime(typeTimeKey)}
																style={{
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'space-between',
																	padding: '8px 12px 8px 16px',
																	cursor: 'pointer',
																}}
															>
																<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
																	<svg
																		style={{
																			width: 12,
																			height: 12,
																			color: 'var(--screener-text-muted)',
																			transform: isTimeExpanded ? 'rotate(90deg)' : 'none',
																			transition: 'transform 0.15s ease',
																		}}
																		fill="currentColor"
																		viewBox="0 0 20 20"
																	>
																		<path
																			fillRule="evenodd"
																			d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
																			clipRule="evenodd"
																		/>
																	</svg>
																	<span style={{ fontSize: 13, fontWeight: 500, color: 'var(--screener-text)' }}>{time}</span>
																	<span style={{ fontSize: 12, color: 'var(--screener-text-muted)' }}>({markets.length})</span>
																</div>
															</div>
															{isTimeExpanded && (
																<div style={{ paddingLeft: 28, paddingRight: 8, paddingBottom: 8 }}>
																	{markets.map((market) => {
																		const isSelected = currentValue === market.value;
																		return (
																			<div
																				key={market.value}
																				onClick={() => handleSelect(market)}
																				style={{
																					display: 'flex',
																					alignItems: 'center',
																					gap: 8,
																					padding: '6px 8px',
																					borderRadius: 6,
																					cursor: 'pointer',
																					backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.12)' : 'transparent',
																				}}
																			>
																				<span
																					style={{
																						fontSize: 12,
																						fontWeight: isSelected ? 600 : 400,
																						color: isSelected ? 'var(--screener-accent)' : 'var(--screener-text)',
																					}}
																				>
																					{market.label}
																				</span>
																			</div>
																		);
																	})}
																</div>
															)}
														</div>
													);
												})}
											</div>
										)}
									</div>
								);
							})
						)}
					</div>
				</div>
			</Popover>
		</>
	);
};
